<template>
    <v-dialog
        v-model="showDialog"
        persistent
        eager
        :width="dialogWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card ref="dialogCard">
            <v-card-title class="px-4" role="region" aria-label="dialog-title">
                <v-container pa-0 fluid>
                    <v-row no-gutters>
                        <v-col>
                            <span class="dialog-form-title">{{
                                dialogTitle
                            }}</span>
                            <help-button :href="helpUrl"></help-button>
                        </v-col>
                        <v-col cols="9">
                            <service-account-info
                                :id="xboId"
                                :status="xboStatus"
                                id-label="XBO Device Id"
                            ></service-account-info>
                        </v-col>
                    </v-row>
                    <v-row
                        v-if="isFormEditing || isNewRecord"
                        no-gutters
                        align="center"
                    >
                        <v-col>
                            <span class="text-body-1 error--text">{{
                                formErrorMessage
                            }}</span>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-title>
            <v-card-text class="pb-2">
                <v-expand-transition>
                    <div v-show="isExistingRecord && !isFormEditing">
                        <device-read-form
                            :value="deviceData"
                            :device-healthy="isHealthy"
                            :device-details-loading="isDeviceLoading"
                            role="form"
                            aria-label="device-read-form"
                        />
                    </div>
                </v-expand-transition>
                <v-expand-transition>
                    <v-form
                        v-show="isFormEditing || isNewRecord"
                        ref="form"
                        v-model="isFormValid"
                        role="form"
                        aria-label="device-edit-form"
                    >
                        <device-edit-form
                            v-model="deviceEditData"
                            :device-details-loading="isDeviceLoading"
                            :has-service-account="hasServiceAccount"
                        >
                        </device-edit-form>
                    </v-form>
                </v-expand-transition>
            </v-card-text>
            <v-card-actions
                class="px-4 pb-3"
                role="region"
                aria-label="dialog-actions"
            >
                <v-container fluid pa-0>
                    <v-row no-gutters>
                        <v-col cols="auto">
                            <v-btn
                                v-if="showDeleteButton"
                                outlined
                                small
                                color="error"
                                :loading="isDeleteLoading"
                                :disabled="
                                    !allowDelete ||
                                        isDeleteLoading ||
                                        isFormDirty
                                "
                                @click="onDeleteClick"
                                >Delete
                            </v-btn>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-if="showUploadButton"
                                        outlined
                                        small
                                        color="accent"
                                        :disabled="!allowUpload"
                                        v-on="on"
                                        @click="onUploadClick"
                                        >File Upload</v-btn
                                    >
                                </template>
                                <span>Upload devices from CSV file</span>
                            </v-tooltip>
                        </v-col>
                        <v-col>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-show="showRebootButton"
                                        outlined
                                        small
                                        color="accent"
                                        class="mr-2"
                                        :loading="isRebootLoading"
                                        :disabled="!allowReboot"
                                        v-on="on"
                                        @click="onRebootClick"
                                        >Reboot
                                    </v-btn>
                                </template>
                                <span>
                                    Will power the device off, then on.
                                </span>
                            </v-tooltip>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        v-show="showResetButton"
                                        outlined
                                        small
                                        color="accent"
                                        :loading="isResetLoading"
                                        :disabled="!allowReset"
                                        v-on="on"
                                        @click="onResetClick"
                                        >Reset
                                    </v-btn>
                                </template>
                                <span>
                                    Will reset the device to the default factory
                                    settings.
                                </span>
                            </v-tooltip>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                v-show="showCloseButton"
                                outlined
                                small
                                :disabled="!allowClose"
                                @click="onCloseClick"
                                >Close</v-btn
                            >
                            <v-btn
                                v-show="showCancelButton"
                                outlined
                                small
                                :disabled="!allowCancel"
                                @click="onCancelClick"
                                >Cancel</v-btn
                            >
                            <v-btn
                                v-show="showEditButton"
                                color="primary"
                                small
                                class="ml-2"
                                :disabled="!allowEdit"
                                @click="onEditClick"
                                >Edit</v-btn
                            >
                            <v-btn
                                v-if="showSaveButton"
                                color="primary"
                                small
                                class="ml-2"
                                :disabled="!allowSave"
                                :loading="isSaveLoading"
                                @click="onSaveClick"
                                >Save</v-btn
                            >
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-actions>
        </v-card>
        <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
    </v-dialog>
</template>

<script>
import { pick } from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import ServiceAccountInfo from '../../spaces/components/ServiceAccountInfo';
import DeviceReadForm from '../forms/DeviceReadForm';
import DeviceEditForm from '../forms/DeviceEditForm';
import HelpButton from '@/components/Buttons/HelpButton';

export default {
    name: 'DeviceDetailsDialog',
    components: {
        'confirmation-dialog': ConfirmationDialog,
        'device-read-form': DeviceReadForm,
        'device-edit-form': DeviceEditForm,
        'service-account-info': ServiceAccountInfo,
        'help-button': HelpButton
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true
        },
        deviceId: {
            type: String,
            required: true
        },
        // force to hide the edit, save, delete buttons
        disableEdits: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            isFormValid: false,
            isFormDirty: false,
            isFormEditing: false,
            // form local fields
            deviceEditData: {},
            formErrorMessage: '',
            // the buttons loading variables
            isSaveLoading: false,
            isDeleteLoading: false,
            isResetLoading: false,
            isRebootLoading: false,
            isDeviceLoading: false
        };
    },
    computed: {
        ...mapState('device', {
            selectedDevice: 'device'
        }),
        ...mapState('device', [
            'preferences',
            'spaceInfo',
            'makeOptions',
            'modelOptions',
            'readHelpUrl',
            'editHelpUrl'
        ]),
        ...mapGetters('venue', ['currentMso']),
        // form control
        isNewRecord() {
            return this.deviceId.length === 0;
        },
        isExistingRecord() {
            return this.deviceId.length > 0;
        },
        // show/allow controls
        allowClose() {
            return !this.isDeviceLoading;
        },
        allowCancel() {
            return !this.isDeviceLoading;
        },
        allowEdit() {
            return (
                this.$can('edit', 'Device') &&
                this.isExistingRecord &&
                !this.isDeviceLoading
            );
        },
        allowCreate() {
            return this.$can('create', 'Device') && this.isNewRecord;
        },
        allowDelete() {
            return (
                this.$can('delete', 'Device') &&
                this.isExistingRecord &&
                !this.isDeviceLoading
            );
        },
        allowSave() {
            return (
                (this.allowEdit || this.allowCreate) &&
                this.isFormDirty &&
                this.isFormValid &&
                !this.isSaveLoading
            );
        },
        allowUpload() {
            return this.$can('create', 'Device');
        },
        allowReboot() {
            return (
                this.$can('control', 'Device') &&
                this.isExistingRecord &&
                this.isHealthy &&
                !this.isRebootLoading
            );
        },
        allowReset() {
            return (
                this.$can('control', 'Device') &&
                this.isExistingRecord &&
                this.isHealthy &&
                !this.isResetLoading
            );
        },
        showCancelButton() {
            return this.isFormEditing || this.isNewRecord;
        },
        showCloseButton() {
            return !this.isFormEditing && !this.isNewRecord;
        },
        showEditButton() {
            return (
                this.isExistingRecord &&
                this.$can('show', 'Buttons/devices/editDevice') &&
                !this.isFormEditing &&
                !this.disableEdits
            );
        },
        showSaveButton() {
            return (
                this.isNewRecord || (this.isFormEditing && !this.disableEdits)
            );
        },
        showDeleteButton() {
            return (
                this.isExistingRecord &&
                this.$can('show', 'Buttons/devices/deleteDevices') &&
                this.isFormEditing &&
                !this.disableEdits
            );
        },
        showUploadButton() {
            return (
                this.isNewRecord &&
                this.$can('show', 'Buttons/devices/uploadDevices') &&
                !this.disableEdits
            );
        },
        showRebootButton() {
            return (
                this.isExistingRecord &&
                !this.isFormEditing &&
                this.$can('show', 'Buttons/devices/rebootDevices')
            );
        },
        showResetButton() {
            return (
                this.isExistingRecord &&
                !this.isFormEditing &&
                this.$can('show', 'Buttons/devices/resetDevices')
            );
        },
        // dialog params
        dialogTitle() {
            if (this.isNewRecord) {
                return 'Add device';
            } else if (this.isFormEditing) {
                return 'Edit device';
            } else {
                return 'Device';
            }
        },
        dialogWidth() {
            if (this.$vuetify.breakpoint.sm) {
                return '95%';
            } else {
                return '500px';
            }
        },
        // form computed fields
        xboId() {
            if (this.selectedDevice) {
                return this.selectedDevice.xboDeviceId || '';
            }
            return '';
        },
        xboStatus() {
            if (this.selectedDevice) {
                return this.selectedDevice.status;
            }
            return '';
        },
        hasServiceAccount() {
            if (this.selectedDevice && this.selectedDevice.serviceAccount) {
                return true;
            } else {
                return false;
            }
        },
        deviceData() {
            return {
                ...pick(this.selectedDevice, [
                    'id',
                    'name',
                    'type',
                    'make',
                    'model',
                    'hostMac',
                    'serialNumber',
                    'tags'
                ]),

                healthStatus: {
                    status: this.selectedDevice.healthCheckStatus,
                    statusDate: this.selectedDevice.healthCheckStatusDate
                },
                talkingGuide:
                    this.preferences.textToSpeech &&
                    this.preferences.textToSpeech.toUpperCase() === 'ON',
                closedCaption:
                    this.preferences.showClosedCaptureStatus &&
                    this.preferences.showClosedCaptureStatus.toUpperCase() ===
                        'ON',
                deviceRoom:
                    this.spaceInfo && this.spaceInfo.room
                        ? `${this.spaceInfo.room} @ ${this.spaceInfo.floor} FLR`
                        : '',
                serviceAccountName: this.selectedDevice.serviceAccount
                    ? this.selectedDevice.serviceAccount.name
                    : ''
            };
        },
        helpUrl() {
            if (this.isNewRecord || this.isFormEditing) {
                return this.editHelpUrl;
            }
            return this.readHelpUrl;
        },
        isHealthy() {
            if (
                this.selectedDevice &&
                this.selectedDevice.healthCheckStatus === 'CONNECTED'
            ) {
                return true;
            }
            return false;
        }
    },
    watch: {
        selectedDevice() {
            this.copyDeviceEditData();
        },
        spaceInfo() {
            this.copyDeviceEditData();
        },
        deviceEditData: {
            handler() {
                this.isFormDirty = true;
            }
        }
    },
    created() {
        this.fetchDeviceData();
    },
    mounted() {
        if (this.isNewRecord) {
            this.copyNewDeviceData();
        }
    },
    methods: {
        // ========================= Vuex Mapping ===================
        ...mapActions('device', [
            'fetchDeviceDetails',
            'resetDevice',
            'createDeviceEntity',
            'updateDeviceEntity',
            'deleteDevice',
            'checkIsDeviceUnique',
            'rebootDevice',
            'resetDeviceSettings'
        ]),
        // ========================= Events =========================
        onCloseClick() {
            this.closeThisDialog();
        },
        onCancelClick() {
            if (this.isFormDirty) {
                // need to confirm cancel changes
                this.$refs.confirmationDialog
                    .open(
                        'Edit Device',
                        'Are you sure, you want to close without saving',
                        'Form data has been modified',
                        {
                            confirmActionText: 'Save changes & close',
                            cancelActionText: 'Cancel changes',
                            showIcon: true
                        }
                    )
                    .then(res => {
                        if (res === true) {
                            this.saveThisDevice();
                        } else {
                            if (this.isExistingRecord) {
                                this.resetDialogData();
                            } else {
                                this.closeThisDialog();
                            }
                        }
                    });
            } else {
                if (this.isExistingRecord) {
                    this.resetDialogData();
                } else {
                    this.closeThisDialog();
                }
            }
        },
        onEditClick() {
            // editable fields are name, room, tag if device has service account
            // if no service account then edit name, hostMac, serialNumber, make, model, room, tags
            this.isFormEditing = true;
            this.setEditFormFocus();
        },
        onSaveClick() {
            this.saveThisDevice();
        },
        onUploadClick() {
            setTimeout(() => {
                this.$emit('upload');
            }, 200);
            this.closeThisDialog();
        },
        onDeleteClick() {
            this.confirmDeviceDelete();
        },
        onResetClick() {
            this.resetThisDevice();
        },
        onRebootClick() {
            this.rebootThisDevice();
        },
        onEscapeKeydown() {
            if (this.showCancelButton) {
                this.onCancelClick();
            } else if (this.showCloseButton) {
                this.onCloseClick();
            }
        },
        // ========================= Others =========================
        confirmDeviceDelete() {
            const deviceBrickMessage = `Deleting a Device attached to a Service Account will render it unusable. And it will need to be returned to ${this.currentMso}.`;
            this.$refs.confirmationDialog
                .open(
                    'Delete Device',
                    'Are you sure, you want to delete this device?',
                    this.hasServiceAccount ? deviceBrickMessage : '',
                    {
                        confirmActionText: 'Yes, delete device',
                        cancelActionText: 'Nevermind',
                        showIcon: true,
                        subMessageClass: 'error--text'
                    }
                )
                .then(res => {
                    if (res === true) {
                        this.deleteThisDevice();
                    }
                });
        },
        resetDialogData() {
            if (this.isFormDirty) {
                this.deviceEditData = {};
            }
            this.isFormDirty = false;
            this.isFormEditing = false;
            this.formErrorMessage = '';
            this.isSaveLoading = false;
            this.isDeleteLoading = false;
            this.isResetLoading = false;
            this.isRebootLoading = false;
            this.setReadFormFocus();
        },
        async closeThisDialog(refreshParentData = true, timeout = 200) {
            setTimeout(() => {
                this.resetDialogData();
                this.resetDevice();
                this.$emit('close-dialog', refreshParentData);
            }, timeout);
        },
        fetchDeviceData() {
            if (this.isExistingRecord) {
                this.isDeviceLoading = true;
                this.fetchDeviceDetails(this.deviceId).finally(() => {
                    this.isDeviceLoading = false;
                });
            }
        },
        copyDeviceEditData() {
            this.deviceEditData = {
                ...pick(this.selectedDevice, [
                    'id',
                    'ownerId',
                    'name',
                    'make',
                    'model',
                    'hostMac',
                    'serialNumber',
                    'tags'
                ])
            };
            // resolve the uppercase/lowercase issue.
            this.deviceEditData.make = this.deviceEditData.make.toUpperCase();
            this.deviceEditData.model = this.deviceEditData.model.toUpperCase();
            setTimeout(() => {
                this.isFormDirty = false;
            }, 100);
        },
        copyNewDeviceData() {
            const defaultMake = this.makeOptions[0];
            const defaultModel = this.modelOptions[0];
            this.deviceEditData = {
                make: defaultMake || '',
                model: defaultModel.name || ''
            };
            setTimeout(() => {
                this.isFormDirty = false;
            }, 100);
        },
        async rebootThisDevice() {
            if (this.isExistingRecord) {
                try {
                    this.isRebootLoading = true;
                    await this.rebootDevice({
                        deviceId: this.selectedDevice.id
                    });
                } finally {
                    this.isRebootLoading = false;
                }
            }
        },
        async resetThisDevice() {
            if (this.isExistingRecord) {
                try {
                    this.isResetLoading = true;
                    await this.resetDeviceSettings({
                        deviceId: this.selectedDevice.id
                    });
                } finally {
                    this.isResetLoading = false;
                }
            }
        },
        async deleteThisDevice() {
            if (this.isExistingRecord) {
                try {
                    this.isDeleteLoading = true;
                    await this.deleteDevice({
                        device: this.selectedDevice
                    });
                    this.closeThisDialog(true);
                } finally {
                    this.isDeleteLoading = false;
                }
            }
        },
        async saveThisDevice() {
            try {
                this.isSaveLoading = true;
                if (this.isExistingRecord && this.isFormDirty) {
                    const isValid = await this.isFormDataValid();
                    if (!isValid) {
                        return;
                    }
                    const payload = {
                        oldDevice: {
                            ...this.selectedDevice,
                            tags: this.selectedDevice.tags,
                            space: this.spaceInfo
                        },
                        newDevice: {
                            ...this.deviceEditData,
                            tags: this.deviceEditData.tags || [],
                            space: this.deviceEditData.room
                        }
                    };
                    await this.updateDeviceEntity(payload);
                    this.closeThisDialog(true);
                } else if (this.isNewRecord && this.isFormDirty) {
                    // need to call the api to create new space
                    const isValid = await this.isFormDataValid();
                    if (!isValid) {
                        return;
                    }
                    const payload = {
                        device: this.deviceEditData,
                        tags: this.deviceEditData.tags || []
                    };
                    await this.createDeviceEntity(payload);
                    this.closeThisDialog(true);
                }
            } finally {
                this.isSaveLoading = false;
            }
        },
        async isFormDataValid() {
            // need to check if device hostMac is unique
            let isUnique = true;
            if (this.isFormDirty && this.deviceData.hostMac) {
                isUnique = await this.checkIsDeviceUnique({
                    id: this.selectedDevice.id,
                    hostMac: this.deviceEditData.hostMac,
                    isNewRecord: this.isNewRecord
                });
            }
            if (!isUnique) {
                this.formErrorMessage =
                    'A device with same Host Mac already exists! Please enter a unique Host Mac address.';
            }
            if (!this.isFormValid || !isUnique) {
                return false;
            } else {
                this.formErrorMessage = '';
                return true;
            }
        },
        setEditFormFocus() {
            this.$nextTick(() => {
                const form = this.$refs.form;
                if (form) {
                    form.$el
                        .querySelector('.v-input input:not([disabled])')
                        .focus();
                }
            });
        },
        setReadFormFocus() {
            this.$nextTick(() => {
                const dialogCard = this.$refs.dialogCard;
                if (dialogCard) {
                    dialogCard.$el
                        .querySelector(
                            '.v-btn:not([disabled]):not([style*="display: none"])'
                        )
                        .focus();
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped></style>
